/**
 * Rex Inbox — Integrations Hub
 * 
 * Manages Gmail + LinkedIn connections.
 * Gmail: Auto-ingests emails → Commercial Briefs.
 * LinkedIn: Monitors personal + RESILIGENT post performance.
 */

import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Mail, Link2, Unlink, RefreshCw, CheckCircle2, XCircle,
  Clock, FileText, AlertTriangle, Zap, Inbox, ArrowRight,
  Linkedin, BarChart3, TrendingUp, TrendingDown, Minus,
  Heart, MessageCircle, Share2, Eye, Users, Trash2,
} from "lucide-react";

export default function RexInbox() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Gmail state
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [connection, setConnection] = useState(null);
  const [ingestionLog, setIngestionLog] = useState([]);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  // LinkedIn state
  const [liLoading, setLiLoading] = useState(true);
  const [liSyncing, setLiSyncing] = useState(false);
  const [liConnection, setLiConnection] = useState(null);
  const [liAnalytics, setLiAnalytics] = useState(null);
  const [liSyncResult, setLiSyncResult] = useState(null);
  const [liPosts, setLiPosts] = useState([]);

  // Needs Review (triage) state — where the resolver's refusals get resolved
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [actioning, setActioning] = useState(null);

  // Proposed Actions state — propose → approve → graduate
  const [proposed, setProposed] = useState([]);
  const [proposedLoading, setProposedLoading] = useState(true);
  const [proposedActioning, setProposedActioning] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("gmail");

  // Check for OAuth callback params
  useEffect(() => {
    if (searchParams.get("connected") === "true") {
      toast.success("Gmail connected successfully!");
      window.history.replaceState({}, "", "/rex-inbox");
    }
    if (searchParams.get("error")) {
      toast.error(`Connection failed: ${searchParams.get("error")}`);
      window.history.replaceState({}, "", "/rex-inbox");
    }
    if (searchParams.get("li_connected") === "true") {
      toast.success("LinkedIn connected successfully!");
      setActiveTab("linkedin");
      window.history.replaceState({}, "", "/rex-inbox");
    }
    if (searchParams.get("li_error")) {
      toast.error(`LinkedIn connection failed: ${searchParams.get("li_error")}`);
      setActiveTab("linkedin");
      window.history.replaceState({}, "", "/rex-inbox");
    }
  }, [searchParams]);

  // ─── Gmail Functions ──────────────────────────────────────────

  const fetchGmailStatus = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, logRes] = await Promise.all([
        fetch(`${API}/oauth/gmail/status`, { credentials: "include" }),
        fetch(`${API}/oauth/gmail/ingestion-log?limit=20`, { credentials: "include" }),
      ]);
      if (statusRes.ok) setConnection(await statusRes.json());
      if (logRes.ok) {
        const data = await logRes.json();
        setIngestionLog(data.logs || []);
      }
    } catch {
      toast.error("Failed to load inbox status");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGmailConnect = () => {
    window.location.href = `${API}/oauth/gmail/connect`;
  };

  // ─── Needs Review (triage) Functions ──────────────────────────

  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await fetch(`${API}/oauth/gmail/pending-confirmations?limit=50`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setPending(data.pending || []);
      }
    } catch {
      /* silent — non-critical surface */
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const handleUndoLearned = async (clientId, learned) => {
    try {
      const res = await fetch(`${API}/oauth/gmail/undo-learned`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId, alias: learned?.alias, contact_email: learned?.contact_email }),
      });
      if (res.ok) toast.success("Reverted what REX learned");
      else toast.error("Undo failed");
    } catch {
      toast.error("Undo failed");
    }
  };

  const handleConfirm = async (brief, decision, clientId = null) => {
    setActioning(brief.brief_id);
    try {
      const res = await fetch(`${API}/oauth/gmail/confirm-client`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief_id: brief.brief_id, decision, client_id: clientId, learn: true }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (decision === "link") {
          toast.success(`Linked to ${data.company_name}`);
          if (data.learned?.summary?.length) {
            toast.message("REX learned this", {
              description: data.learned.summary.join(" · "),
              action: { label: "Undo", onClick: () => handleUndoLearned(data.client_id, data.learned) },
            });
          }
        } else if (decision === "new") {
          toast.success(`Created new client: ${data.company_name}`);
        } else {
          toast.info("Dismissed");
        }
        setPending((p) => p.filter((b) => b.brief_id !== brief.brief_id));
      } else {
        toast.error(data.detail || "Action failed");
      }
    } catch {
      toast.error("Action failed");
    } finally {
      setActioning(null);
    }
  };

  // ─── Proposed Actions (propose → approve → graduate) ──────────

  const fetchProposed = useCallback(async () => {
    setProposedLoading(true);
    try {
      const res = await fetch(`${API}/oauth/gmail/proposed-actions?limit=50`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setProposed(data.pending || []);
      }
    } catch {
      /* silent — non-critical surface */
    } finally {
      setProposedLoading(false);
    }
  }, []);

  const handleApplyProposed = async (brief, decision) => {
    setProposedActioning(brief.brief_id);
    try {
      const res = await fetch(`${API}/oauth/gmail/apply-proposed`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief_id: brief.brief_id, decision }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (decision === "approve") {
          toast.success(`Applied ${data.applied_count} action${data.applied_count === 1 ? "" : "s"} for ${brief.account_name}`);
        } else {
          toast.info("Dismissed");
        }
        setProposed((p) => p.filter((b) => b.brief_id !== brief.brief_id));
      } else {
        toast.error(data.detail || "Action failed");
      }
    } catch {
      toast.error("Action failed");
    } finally {
      setProposedActioning(null);
    }
  };

  const handleGmailDisconnect = async () => {
    if (!confirm("Disconnect Gmail? Email ingestion will stop.")) return;
    try {
      const res = await fetch(`${API}/oauth/gmail/disconnect`, { method: "POST", credentials: "include" });
      if (res.ok) {
        toast.success("Gmail disconnected");
        setConnection({ connected: false });
      }
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const handleGmailSync = async () => {
    setSyncing(true);
    setLastSyncResult(null);
    try {
      const res = await fetch(`${API}/oauth/gmail/sync`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const result = await res.json();
        setLastSyncResult(result);
        if (result.briefs_created?.length > 0) {
          toast.success(`Created ${result.briefs_created.length} new briefs from emails!`);
        } else if (result.processed === 0 && result.skipped === 0) {
          toast.info("No new emails to process");
        } else {
          toast.success(`Processed ${result.processed} emails, skipped ${result.skipped}`);
        }
        fetchGmailStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Sync failed");
      }
    } catch {
      toast.error("Sync failed - network error");
    } finally {
      setSyncing(false);
    }
  };

  const handleReanalyze = async () => {
    if (!confirm("Re-analyze all existing email briefs with AI? This will upgrade their intelligence quality.")) return;
    setReanalyzing(true);
    try {
      const res = await fetch(`${API}/oauth/gmail/reanalyze-briefs`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Re-analyzed ${result.reanalyzed} of ${result.total_briefs} briefs`);
        fetchGmailStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Re-analysis failed");
      }
    } catch {
      toast.error("Re-analysis failed - network error");
    } finally {
      setReanalyzing(false);
    }
  };

  const handleDeleteIngestion = async (logId, briefId) => {
    if (!confirm("Delete this ingestion entry and its associated brief?")) return;
    try {
      const res = await fetch(`${API}/oauth/gmail/ingestion/${logId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        toast.success("Entry deleted");
        setIngestionLog(prev => prev.filter(l => l.log_id !== logId));
      } else {
        toast.error("Failed to delete");
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  // ─── LinkedIn Functions ───────────────────────────────────────

  const fetchLinkedInStatus = useCallback(async () => {
    setLiLoading(true);
    try {
      const res = await fetch(`${API}/oauth/linkedin/status`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLiConnection(data);

        // If connected, also fetch analytics summary and recent posts
        if (data.connected) {
          const [analyticsRes, postsRes] = await Promise.all([
            fetch(`${API}/oauth/linkedin/analytics/summary?days=30`, { credentials: "include" }),
            fetch(`${API}/oauth/linkedin/posts?limit=10`, { credentials: "include" }),
          ]);
          if (analyticsRes.ok) setLiAnalytics(await analyticsRes.json());
          if (postsRes.ok) {
            const pd = await postsRes.json();
            setLiPosts(pd.posts || []);
          }
        }
      }
    } catch {
      // Silently handle — LinkedIn may not be set up yet
    } finally {
      setLiLoading(false);
    }
  }, []);

  const handleLinkedInConnect = () => {
    window.location.href = `${API}/oauth/linkedin/connect`;
  };

  const handleLinkedInDisconnect = async () => {
    if (!confirm("Disconnect LinkedIn? Post monitoring will stop.")) return;
    try {
      const res = await fetch(`${API}/oauth/linkedin/disconnect`, { method: "POST", credentials: "include" });
      if (res.ok) {
        toast.success("LinkedIn disconnected");
        setLiConnection({ connected: false });
        setLiAnalytics(null);
        setLiPosts([]);
      }
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const handleLinkedInSync = async () => {
    setLiSyncing(true);
    setLiSyncResult(null);
    try {
      const res = await fetch(`${API}/oauth/linkedin/sync-posts`, { method: "POST", credentials: "include" });
      if (res.ok) {
        const result = await res.json();
        setLiSyncResult(result);
        toast.success(`Synced ${result.total_synced} posts from LinkedIn`);
        fetchLinkedInStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || "LinkedIn sync failed");
      }
    } catch {
      toast.error("LinkedIn sync failed - network error");
    } finally {
      setLiSyncing(false);
    }
  };

  useEffect(() => {
    fetchGmailStatus();
    fetchLinkedInStatus();
    fetchPending();
    fetchProposed();
  }, [fetchGmailStatus, fetchLinkedInStatus, fetchPending, fetchProposed]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
  };

  const formatNumber = (n) => {
    if (!n && n !== 0) return "0";
    if (n >= 1000) return `${(n/1000).toFixed(1)}k`;
    return n.toString();
  };

  // ─── Render ───────────────────────────────────────────────────

  return (
    <div className="p-6" data-testid="rex-inbox-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <Inbox className="w-5 h-5 text-blue-600" />
              <h1 className="text-xl font-bold tracking-tight text-slate-800">Rex Inbox</h1>
              <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600 ml-1">INTEGRATIONS</Badge>
            </div>
            <p className="text-sm text-slate-500">
              Connect external channels — Rex captures commercial signals and monitors content performance
            </p>
          </div>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-1 mb-6 bg-slate-100 rounded-lg p-1 w-fit" data-testid="inbox-tabs">
        <button
          onClick={() => setActiveTab("gmail")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === "gmail"
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="gmail-tab"
        >
          <Mail className="w-4 h-4" /> Gmail
          {connection?.connected && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
        </button>
        <button
          onClick={() => setActiveTab("review")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === "review"
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="needs-review-tab"
        >
          <AlertTriangle className="w-4 h-4" /> Needs Review
          {pending.length > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold"
              data-testid="needs-review-count"
            >
              {pending.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("proposed")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === "proposed"
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="proposed-tab"
        >
          <Zap className="w-4 h-4" /> Proposed
          {proposed.length > 0 && (
            <span
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold"
              data-testid="proposed-count"
            >
              {proposed.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("linkedin")}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === "linkedin"
              ? "bg-white text-slate-800 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="linkedin-tab"
        >
          <Linkedin className="w-4 h-4" /> LinkedIn
          {liConnection?.connected && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
        </button>
      </div>

      <div className="max-w-[960px]">
        {/* ──────────────────── GMAIL TAB ──────────────────── */}
        {activeTab === "gmail" && (
          <>
            {/* Gmail Connection Card */}
            <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6 shadow-sm" data-testid="gmail-connection-card">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    connection?.connected ? "bg-emerald-50 border border-emerald-200" : "bg-slate-100 border border-slate-200"
                  }`}>
                    <Mail className={`w-6 h-6 ${connection?.connected ? "text-emerald-600" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-slate-800 mb-1">Gmail Connection</h2>
                    {connection?.connected ? (
                      <>
                        <p className="text-sm text-emerald-600 flex items-center gap-1.5 mb-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Connected to {connection.email}
                        </p>
                        <p className="text-xs text-slate-500">Last sync: {connection.last_sync ? formatDate(connection.last_sync) : "Never"}</p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">Connect your Gmail to enable automatic email ingestion</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {connection?.connected ? (
                    <>
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleGmailSync} disabled={syncing} data-testid="gmail-sync-btn">
                        {syncing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</> : <><Zap className="w-4 h-4 mr-2" /> Sync Now</>}
                      </Button>
                      <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={handleReanalyze} disabled={reanalyzing} data-testid="gmail-reanalyze-btn">
                        {reanalyzing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Re-analyze</>}
                      </Button>
                      <Button variant="outline" className="border-slate-300 text-slate-600" onClick={handleGmailDisconnect}>
                        <Unlink className="w-4 h-4 mr-2" /> Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleGmailConnect} data-testid="gmail-connect-btn">
                      <Link2 className="w-4 h-4 mr-2" /> Connect Gmail
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Gmail Sync Result */}
            {lastSyncResult && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 mb-6" data-testid="gmail-sync-result">
                <h3 className="text-sm font-semibold text-blue-700 mb-3">Last Sync Result</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3 text-center border border-slate-200">
                    <p className="text-2xl font-bold text-emerald-600">{lastSyncResult.processed}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Processed</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-slate-200">
                    <p className="text-2xl font-bold text-slate-500">{lastSyncResult.skipped}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Skipped</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-slate-200">
                    <p className="text-2xl font-bold text-blue-600">{lastSyncResult.briefs_created?.length || 0}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Briefs Created</p>
                  </div>
                </div>
                {lastSyncResult.briefs_created?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-600 font-semibold">New Briefs:</p>
                    {lastSyncResult.briefs_created.map((brief) => (
                      <div key={brief.brief_id} className="flex items-center justify-between bg-white rounded-lg p-3 hover:bg-slate-50 cursor-pointer transition-all border border-slate-200"
                        onClick={() => navigate(`/commercial-briefs/${brief.brief_id}`)}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <div>
                            <p className="text-sm text-slate-800 font-medium">{brief.account_name || "Unknown"}</p>
                            {brief.hypothesis && (
                              <p className="text-xs text-slate-600 mt-0.5 line-clamp-1 max-w-[500px]">{brief.hypothesis}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className={`text-[9px] ${
                                brief.signal_strength === "high" ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                                : brief.signal_strength === "medium" ? "border-amber-300 text-amber-700 bg-amber-50"
                                : "border-slate-300 text-slate-600"
                              }`}>{brief.signal_strength}</Badge>
                              <span className="text-[10px] text-slate-500">{brief.signal_type?.replace(/_/g, " ")}</span>
                              {brief.is_bcc && <Badge variant="outline" className="text-[9px] border-violet-300 text-violet-600 bg-violet-50">BCC</Badge>}
                              {brief.client_matched && <span className="text-[10px] text-emerald-600">{brief.client_auto_created ? "Client created" : "Client matched"}</span>}
                            </div>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* How It Works */}
            {!connection?.connected && (
              <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">How Rex Inbox Works</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { step: "1", title: "Connect Gmail", desc: "Securely authorize Rex to read your inbox" },
                    { step: "2", title: "AI Analysis", desc: "GPT-4o extracts commercial intelligence from each email" },
                    { step: "3", title: "Smart Briefs", desc: "Creates rich briefs with hypothesis, insights & next steps" },
                  ].map((s) => (
                    <div key={s.step} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <span className="text-blue-600 font-bold text-sm">{s.step}</span>
                      </div>
                      <div>
                        <p className="text-sm text-slate-800 font-medium">{s.title}</p>
                        <p className="text-xs text-slate-500">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ingestion Log */}
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="ingestion-log">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800">Ingestion History</h3>
                <span className="text-xs text-slate-500">{ingestionLog.length} entries</span>
              </div>
              {ingestionLog.length === 0 ? (
                <div className="text-center py-10">
                  <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No emails ingested yet</p>
                  <p className="text-xs text-slate-400">Connect Gmail and sync to start capturing commercial signals</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {ingestionLog.map((log) => (
                    <div key={log.log_id} className={`rounded-lg p-3 ${log.skipped ? "bg-slate-50 border border-slate-200" : "bg-white border border-slate-200"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          {log.skipped ? <XCircle className="w-4 h-4 text-slate-400 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                          <div className="min-w-0">
                            {log.skipped ? (
                              <p className="text-xs text-slate-500">Skipped: {log.skip_reason}</p>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-medium text-slate-700">{log.account_name || log.brief_details?.account_name || "Brief created"}</span>
                                  <Badge variant="outline" className={`text-[9px] ${
                                    (log.signal_strength || log.brief_details?.signal_strength) === "high" ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                                    : (log.signal_strength || log.brief_details?.signal_strength) === "medium" ? "border-amber-300 text-amber-700 bg-amber-50"
                                    : "border-slate-300 text-slate-600"
                                  }`}>{log.signal_strength || log.brief_details?.signal_strength || "low"}</Badge>
                                  <span className="text-[10px] text-slate-500">{(log.signal_type || log.brief_details?.signal_type || "").replace(/_/g, " ")}</span>
                                  {log.client_matched && <span className="text-[10px] text-emerald-600 font-medium">Client matched</span>}
                                </div>
                                {(log.hypothesis_preview || log.brief_details?.current_hypothesis) && (
                                  <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{log.hypothesis_preview || log.brief_details?.current_hypothesis}</p>
                                )}
                              </>
                            )}
                            <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(log.created_at)}</p>
                          </div>
                        </div>
                        {!log.skipped && log.brief_id && (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="sm" variant="ghost" className="text-blue-600 h-7 px-2" onClick={() => navigate(`/commercial-briefs/${log.brief_id}`)}>
                              View <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-500 hover:bg-red-50 h-7 px-1.5" onClick={() => handleDeleteIngestion(log.log_id, log.brief_id)} data-testid={`delete-ingestion-${log.log_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        {log.skipped && (
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-500 hover:bg-red-50 h-7 px-1.5 shrink-0" onClick={() => handleDeleteIngestion(log.log_id)} data-testid={`delete-ingestion-${log.log_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ──────────────────── NEEDS REVIEW TAB ──────────────────── */}
        {activeTab === "review" && (
          <div data-testid="needs-review-panel">
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 mb-5">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Emails REX couldn't place with confidence</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Rather than guess and risk a duplicate client, REX asks. Confirm who it is and REX learns it —
                    future emails from this sender resolve on their own. Everything it learns is visible and reversible.
                  </p>
                </div>
              </div>
            </div>

            {pendingLoading ? (
              <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
            ) : pending.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="needs-review-empty">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">Nothing to review</p>
                <p className="text-xs text-slate-400 mt-1">Every ingested email has been confidently matched.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="needs-review-list">
                {pending.map((b) => (
                  <ReviewCard
                    key={b.brief_id}
                    brief={b}
                    actioning={actioning === b.brief_id}
                    onConfirm={handleConfirm}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ──────────────────── PROPOSED ACTIONS TAB ──────────────────── */}
        {activeTab === "proposed" && (
          <div data-testid="proposed-panel">
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 mb-5">
              <div className="flex items-start gap-2.5">
                <Zap className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">REX proposed these from your emails</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    For each resolved email, REX proposes the follow-through — a stage change, a task, a reminder.
                    Nothing is applied until you approve. As you approve, REX earns the trust to do it hands-off.
                  </p>
                </div>
              </div>
            </div>

            {proposedLoading ? (
              <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
            ) : proposed.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="proposed-empty">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">No proposals waiting</p>
                <p className="text-xs text-slate-400 mt-1">New emails will surface their proposed follow-through here.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="proposed-list">
                {proposed.map((b) => (
                  <ProposedCard
                    key={b.brief_id}
                    brief={b}
                    actioning={proposedActioning === b.brief_id}
                    onApply={handleApplyProposed}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ──────────────────── LINKEDIN TAB ──────────────────── */}
        {activeTab === "linkedin" && (
          <>
            {/* LinkedIn Connection Card */}
            <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6 shadow-sm" data-testid="linkedin-connection-card">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    liConnection?.connected ? "bg-blue-50 border border-blue-200" : "bg-slate-100 border border-slate-200"
                  }`}>
                    <Linkedin className={`w-6 h-6 ${liConnection?.connected ? "text-blue-700" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-slate-800 mb-1">LinkedIn Connection</h2>
                    {liConnection?.connected ? (
                      <>
                        <p className="text-sm text-blue-700 flex items-center gap-1.5 mb-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Connected as {liConnection.member_name || liConnection.member_email}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span>Org ID: {liConnection.org_id}</span>
                          <span>Posts tracked: {liConnection.posts_tracked || 0}</span>
                          <span>Last sync: {liConnection.last_sync ? formatDate(liConnection.last_sync) : "Never"}</span>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500">
                        Connect LinkedIn to monitor personal + RESILIGENT post performance
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {liConnection?.connected ? (
                    <>
                      <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={handleLinkedInSync} disabled={liSyncing} data-testid="linkedin-sync-btn">
                        {liSyncing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...</> : <><Zap className="w-4 h-4 mr-2" /> Sync Posts</>}
                      </Button>
                      <Button variant="outline" className="border-slate-300 text-slate-600" onClick={handleLinkedInDisconnect} data-testid="linkedin-disconnect-btn">
                        <Unlink className="w-4 h-4 mr-2" /> Disconnect
                      </Button>
                    </>
                  ) : (
                    <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={handleLinkedInConnect} data-testid="linkedin-connect-btn">
                      <Linkedin className="w-4 h-4 mr-2" /> Connect LinkedIn
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* LinkedIn Sync Result */}
            {liSyncResult && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 mb-6" data-testid="linkedin-sync-result">
                <h3 className="text-sm font-semibold text-blue-700 mb-3">Sync Result</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-3 text-center border border-slate-200">
                    <p className="text-2xl font-bold text-blue-700">{liSyncResult.personal_count}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Personal Posts</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-slate-200">
                    <p className="text-2xl font-bold text-blue-600">{liSyncResult.org_count}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">RESILIGENT Posts</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-slate-200">
                    <p className="text-2xl font-bold text-emerald-600">{liSyncResult.total_synced}</p>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Synced</p>
                  </div>
                </div>
                {liSyncResult.errors?.length > 0 && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-lg p-2 border border-amber-200">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    {liSyncResult.errors.join("; ")}
                  </div>
                )}
              </div>
            )}

            {/* Analytics Dashboard */}
            {liConnection?.connected && liAnalytics && liAnalytics.total_posts > 0 && (
              <div className="space-y-6 mb-6">
                {/* Engagement Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="linkedin-analytics-cards">
                  <MetricCard
                    label="Total Posts"
                    value={liAnalytics.total_posts}
                    icon={<FileText className="w-4 h-4 text-blue-600" />}
                  />
                  <MetricCard
                    label="Total Engagement"
                    value={formatNumber(liAnalytics.combined?.total_engagement || 0)}
                    icon={<Heart className="w-4 h-4 text-rose-500" />}
                  />
                  <MetricCard
                    label="Comments"
                    value={formatNumber(liAnalytics.combined?.total_comments || 0)}
                    icon={<MessageCircle className="w-4 h-4 text-amber-500" />}
                  />
                  <MetricCard
                    label="Shares"
                    value={formatNumber(liAnalytics.combined?.total_shares || 0)}
                    icon={<Share2 className="w-4 h-4 text-emerald-500" />}
                  />
                </div>

                {/* Source Comparison */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="personal-engagement">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-4 h-4 text-blue-600" />
                      <h3 className="text-sm font-semibold text-slate-800">Personal Profile</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-lg font-bold text-slate-800">{liAnalytics.personal?.post_count || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Posts</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-rose-500">{formatNumber(liAnalytics.personal?.total_likes || 0)}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Likes</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-emerald-600">{formatNumber(liAnalytics.personal?.total_engagement || 0)}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Engagement</p>
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="org-engagement">
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 className="w-4 h-4 text-blue-700" />
                      <h3 className="text-sm font-semibold text-slate-800">RESILIGENT Page</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-lg font-bold text-slate-800">{liAnalytics.organization?.post_count || 0}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Posts</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-rose-500">{formatNumber(liAnalytics.organization?.total_likes || 0)}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Likes</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-emerald-600">{formatNumber(liAnalytics.organization?.total_engagement || 0)}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Engagement</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content Type Distribution */}
                {liAnalytics.content_type_distribution && Object.keys(liAnalytics.content_type_distribution).length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="content-type-dist">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Content Type Distribution</h3>
                    <div className="flex gap-3 flex-wrap">
                      {Object.entries(liAnalytics.content_type_distribution).map(([type, count]) => (
                        <div key={type} className="bg-slate-50 rounded-lg px-4 py-2 border border-slate-200">
                          <p className="text-sm font-semibold text-slate-800 capitalize">{type}</p>
                          <p className="text-xs text-slate-500">{count} posts</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Posts */}
                {liAnalytics.top_posts?.length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="top-posts">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Top Performing Posts</h3>
                    <div className="space-y-3">
                      {liAnalytics.top_posts.slice(0, 5).map((post, idx) => (
                        <div key={post.post_urn || idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                            <span className="text-blue-700 font-bold text-xs">{idx + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800 line-clamp-2">{post.commentary || "(No text)"}</p>
                            <div className="flex items-center gap-3 mt-1.5">
                              <Badge variant="outline" className="text-[9px] capitalize">{post.source}</Badge>
                              <Badge variant="outline" className="text-[9px] capitalize">{post.content_type}</Badge>
                              <span className="text-[10px] text-slate-500">{formatDate(post.created_at)}</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="flex items-center gap-2 text-xs text-slate-600">
                              <span className="flex items-center gap-0.5"><Heart className="w-3 h-3 text-rose-400" />{post.engagement?.likes || 0}</span>
                              <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3 text-amber-400" />{post.engagement?.comments || 0}</span>
                              <span className="flex items-center gap-0.5"><Share2 className="w-3 h-3 text-emerald-400" />{post.engagement?.shares || 0}</span>
                            </div>
                            <p className="text-[10px] text-blue-600 font-semibold mt-0.5">Score: {post.engagement_score}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recent Posts List */}
            {liConnection?.connected && liPosts.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm mb-6" data-testid="recent-posts">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Recent Posts</h3>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {liPosts.map((post, idx) => (
                    <div key={post.post_urn || idx} className="flex items-center justify-between rounded-lg p-3 bg-slate-50 border border-slate-200">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Linkedin className={`w-4 h-4 shrink-0 ${post.source === "organization" ? "text-blue-700" : "text-blue-500"}`} />
                        <div className="min-w-0">
                          <p className="text-xs text-slate-700 truncate">{(post.commentary || "(No text)").slice(0, 100)}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className="text-[8px] capitalize">{post.source}</Badge>
                            <Badge variant="outline" className="text-[8px] capitalize">{post.content_type}</Badge>
                            <span className="text-[10px] text-slate-400">{formatDate(post.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      {post.engagement && (
                        <div className="flex items-center gap-2 text-xs text-slate-500 shrink-0 ml-2">
                          <span><Heart className="w-3 h-3 inline text-rose-400" /> {post.engagement.likes || 0}</span>
                          <span><MessageCircle className="w-3 h-3 inline text-amber-400" /> {post.engagement.comments || 0}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How It Works (not connected) */}
            {!liConnection?.connected && (
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">How LinkedIn Monitoring Works</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { step: "1", title: "Connect LinkedIn", desc: "Authorize Rex to read your personal and RESILIGENT page posts" },
                    { step: "2", title: "Sync Posts", desc: "Rex fetches recent posts with engagement metrics (likes, comments, shares)" },
                    { step: "3", title: "Learn & Optimise", desc: "Performance data feeds into Content Studio to improve future content" },
                  ].map((s) => (
                    <div key={s.step} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <span className="text-blue-700 font-bold text-sm">{s.step}</span>
                      </div>
                      <div>
                        <p className="text-sm text-slate-800 font-medium">{s.title}</p>
                        <p className="text-xs text-slate-500">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state when connected but no posts */}
            {liConnection?.connected && liPosts.length === 0 && !liSyncResult && (
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-center">
                <Linkedin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No posts synced yet</p>
                <p className="text-xs text-slate-400 mb-4">Click "Sync Posts" to fetch your LinkedIn content</p>
                <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={handleLinkedInSync} disabled={liSyncing}>
                  <Zap className="w-4 h-4 mr-2" /> Sync Now
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function MetricCard({ label, value, icon, trend }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        {icon}
        {trend === "up" && <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />}
        {trend === "down" && <TrendingDown className="w-3.5 h-3.5 text-rose-500" />}
      </div>
      <p className="text-xl font-bold text-slate-800">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}


function ReviewCard({ brief, actioning, onConfirm }) {
  const prov = brief.email_provenance || {};
  const candidates = brief.client_match_candidates || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`review-card-${brief.brief_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-sm font-semibold text-slate-800 truncate">
              {brief.account_name || brief.client_match_reference || "Unknown sender"}
            </span>
            {prov.is_bcc && <Badge variant="outline" className="text-[9px] border-blue-200 text-blue-600">BCC</Badge>}
          </div>
          <p className="text-xs text-slate-500 truncate">{prov.subject || brief.current_hypothesis}</p>
          {prov.from && <p className="text-[11px] text-slate-400 mt-0.5">{prov.from}</p>}
        </div>
        <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-600 shrink-0" data-testid={`review-status-${brief.brief_id}`}>{candidates.length > 0 ? "AMBIGUOUS" : "NO MATCH"}</Badge>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100">
        <p className="text-[11px] font-medium text-slate-500 mb-2">Which client is this?</p>
        <div className="flex flex-wrap gap-2">
          {candidates.length === 0 && (
            <span className="text-xs text-slate-400">No close matches. Rex has not created a client — confirm below.</span>
          )}
          {candidates.map((c) => (
            <Button
              key={c.client_id}
              size="sm"
              variant="outline"
              disabled={actioning}
              onClick={() => onConfirm(brief, "link", c.client_id)}
              className="h-8 text-xs border-slate-300 hover:border-blue-400 hover:bg-blue-50"
              data-testid={`review-link-${c.client_id}`}
            >
              {c.company_name}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button
            size="sm"
            variant="ghost"
            disabled={actioning}
            onClick={() => onConfirm(brief, "new")}
            className="h-8 text-xs text-emerald-600 hover:bg-emerald-50"
            data-testid={`review-new-${brief.brief_id}`}
          >
            <Zap className="w-3.5 h-3.5 mr-1" /> It's a new client
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={actioning}
            onClick={() => onConfirm(brief, "ignore")}
            className="h-8 text-xs text-slate-400 hover:bg-slate-50"
            data-testid={`review-ignore-${brief.brief_id}`}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" /> Ignore
          </Button>
        </div>
      </div>
    </div>
  );
}


const ACTION_ICON = {
  status_change: TrendingUp,
  create_task: FileText,
  set_reminder: Clock,
};

function ProposedCard({ brief, actioning, onApply }) {
  const prov = brief.email_provenance || {};
  const actions = brief.proposed_actions?.actions || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`proposed-card-${brief.brief_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-sm font-semibold text-slate-800 truncate">{brief.account_name || "Client"}</span>
          </div>
          <p className="text-xs text-slate-500 truncate">{prov.subject || brief.current_hypothesis}</p>
        </div>
        <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600 shrink-0">PROPOSED</Badge>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
        {actions.map((a) => {
          const Icon = ACTION_ICON[a.type] || Zap;
          return (
            <div key={a.id} className="flex items-start gap-2.5" data-testid={`proposed-action-${a.id}`}>
              <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="w-3.5 h-3.5 text-slate-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-700">{a.label}</p>
                {a.reason && <p className="text-[11px] text-slate-400">{a.reason}</p>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
        <Button
          size="sm"
          disabled={actioning}
          onClick={() => onApply(brief, "approve")}
          className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
          data-testid={`proposed-approve-${brief.brief_id}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve all
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={actioning}
          onClick={() => onApply(brief, "dismiss")}
          className="h-8 text-xs text-slate-400 hover:bg-slate-50"
          data-testid={`proposed-dismiss-${brief.brief_id}`}
        >
          <XCircle className="w-3.5 h-3.5 mr-1" /> Dismiss
        </Button>
      </div>
    </div>
  );
}
